import proj4 from "proj4";
proj4.defs(
  "EPSG:6933",
  "+proj=cea +lon_0=0 +lat_ts=30 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs"
);
import * as THREE from "three";

export function geometryToJSON(geometry) {
  return {
    // Essential data
  
    vertices: Array.from(geometry.attributes.position.array),
    indices: geometry.index ? Array.from(geometry.index.array) : null,
    normals: geometry.attributes.normal ? Array.from(geometry.attributes.normal.array) : null,
    
    // Metadata
    userData: geometry.userData,
    uuid: geometry.uuid,
    type: geometry.type,
    
    // Useful for Revit
    vertexCount: geometry.attributes.position.count,
    triangleCount: geometry.index ? geometry.index.count / 3 : 0,
    
    // Bounding info
    boundingBox: geometry.boundingBox ? {
      min: {
        x: geometry.boundingBox.min.x,
        y: geometry.boundingBox.min.y,
        z: geometry.boundingBox.min.z
      },
      max: {
        x: geometry.boundingBox.max.x,
        y: geometry.boundingBox.max.y,
        z: geometry.boundingBox.max.z
      }
    } : null
  };
}

export function CacheGeometry(ne,sw,crs, cacheName, version, data){
  const CACHE_KEY = cacheName;
  const CACHE_VERSION = version;
  saveToCache(GetCacheKey(ne,sw,crs, CACHE_KEY,CACHE_VERSION),data,CACHE_VERSION)

}

export function GetCacheKey (ne, sw, crs,CACHE_KEY, CACHE_VERSION )
{
  return `${CACHE_KEY}_${CACHE_VERSION}_${ne.lng}_${ne.lat}_${sw.lng}_${sw.lat}_${crs}`;
};


function saveToCache(key, data, CACHE_VERSION)
{
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
    //console.log('💾 Geometry cached successfully');
  } catch (error) {
    console.warn('Failed to cache geometry:', error);
    // If localStorage is full, clear old caches
    if (error.name === 'QuotaExceededError') {
      localStorage.removeItem(key);
    }
  }
};

export function ClearCache(keyword)
 {
  try{
      for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
       if (key.startsWith(keyword)) {
        localStorage.removeItem(key);
      }
    }
  }
  catch(error)
  {
    console.warn("Failed to clear cache, cache key ", key, error)
  }

 }
export function LoadFromCache (key, CACHE_VERSION, maxAge = 1000 * 60 * 60 * 24) 
 { // 24 hour default
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp, version } = JSON.parse(cached);
    
    // Check version and age
    if (version !== CACHE_VERSION) {
      localStorage.removeItem(key);
      return null;
    }

    if (Date.now() - timestamp > maxAge) {
      localStorage.removeItem(key);
      return null;
    }

   // console.log('Loaded geometry from cache');
    return data;
  } catch (error) {
    console.warn('Failed to load from cache:', error);
    return null;
  }
};

export function Equalareaprojection(mapBounds)
{
  const WGS84 = "EPSG:4326";
  const EQUAL_AREA = "EPSG:6933";
  const { lat: north, lng: east } = mapBounds._ne;
  const { lat: south, lng: west } = mapBounds._sw;

  // Project corners to meters
  const [x1, y1] = proj4(WGS84, EQUAL_AREA, [west, south]);
  const [x2, y2] = proj4(WGS84, EQUAL_AREA, [east, north]);

  // Compute area of rectangle (meters²)
  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);
  const area_m2 = width * height;
  return area_m2/1000000;

}

export // Splits a list of coordinates into multiple polygons
function splitPolygons (coords)
{
  let polygons = [];
  let current = [];
  console.log(coords);
  // Helper to compare 2 points
  const samePoint = (a, b) =>
    Math.abs(a.x - b.x) < 0.0001 && Math.abs(a.y - b.y) < 0.0001;

  for (let i = 0; i < coords.length; i++) {
    const pt = coords[i];
   

    // If we meet the start point again, polygon is complete
    if (current.length > 1 && samePoint(pt, current[0])) {
      current.push(pt);

      polygons.push([...current]); // store closed polygon
      current = []; // start next polygon
    } else {
      current.push(pt);
    }
  }

  if (current.length > 0) {
    if (!isPolygonClosed(current)) {
      current.push({ ...current[0] });
    }
    polygons.push(current);
  }

  return polygons;
};


function getSignedArea(ring) {
  let area = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    area += ring[i][0] * ring[j][1];
    area -= ring[j][0] * ring[i][1];
  }
  return area / 2;
}

function ensureCCW(ring) {
  return getSignedArea(ring) < 0 ? [...ring].reverse() : ring; // CCW = positive area
}

function ensureCW(ring) {
  return getSignedArea(ring) > 0 ? [...ring].reverse() : ring; // CW = negative area
}

// ── Updated function ─────────────────────────────────────────────
export function CreateShapegeometryWithMultipleVoids(mainringcoords, holesArray) {
  if (!mainringcoords || mainringcoords.length < 3) {
    console.warn("CreateShapegeometryWithMultipleVoids: insufficient points:", mainringcoords?.length);
    return null;
  }

  try {
 
    const exteriorRing = ensureCCW(mainringcoords);

    const shape = new THREE.Shape();
    shape.moveTo(exteriorRing[0][0], exteriorRing[0][1]);
    for (let i = 1; i < exteriorRing.length; i++) {
      const coord = exteriorRing[i];
      if (!coord || !isFinite(coord[0]) || !isFinite(coord[1])) {
        console.warn("Invalid coord in main ring at index", i, coord);
        continue;
      }
      shape.lineTo(coord[0], coord[1]);
    }
    shape.closePath();

    if (holesArray && holesArray.length > 0) {
      holesArray.forEach((holeCoords, holeIndex) => {
        if (!holeCoords || holeCoords.length < 3) {
          console.warn(`Hole ${holeIndex} has insufficient points:`, holeCoords?.length);
          return;
        }
        const hole = ensureCW(holeCoords);

        const path = new THREE.Path();
        path.moveTo(hole[0][0], hole[0][1]);
        for (let i = 1; i < hole.length; i++) {
          const pt = hole[i];
          if (!pt || !isFinite(pt[0]) || !isFinite(pt[1])) {
            console.warn(`Invalid coord in hole ${holeIndex} at index`, i, pt);
            continue;
          }
          path.lineTo(pt[0], pt[1]);
        }
        path.closePath();
        shape.holes.push(path);
      });
    }

    const geometry = new THREE.ShapeGeometry(shape);
    const positions = geometry.attributes.position;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      positions.setXYZ(i, x, 0, -y);
    }

    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    return geometry;

  } catch (err) {
    console.error("CreateShapegeometryWithMultipleVoids failed:", err);
    console.error("Main ring sample:", mainringcoords?.slice(0, 4));
    return null;
  }
}


export function CreateShapegeometry(coords) {

  // ✅ Guard: need at least 3 points
  if (!coords || coords.length < 3) {
    console.warn("CreateShapegeometry: insufficient points:", coords?.length);
    return null;
  }

  try {
    const shape = new THREE.Shape();

    shape.moveTo(coords[0][0], coords[0][1]);
    for (let i = 1; i < coords.length; i++) {
      const coord = coords[i];

      
      if (!coord || !isFinite(coord[0]) || !isFinite(coord[1])) {
        console.warn("Invalid coord at index", i, coord);
        continue;
      }
      shape.lineTo(coord[0], coord[1]);
    }
    shape.closePath(); 

    const geometry = new THREE.ShapeGeometry(shape);
    const positions = geometry.attributes.position;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      positions.setXYZ(i, x, 0, -y);
    }

    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    return geometry;

  } catch (err) {
    // ✅ Now logs the REAL error
    console.error("CreateShapegeometry failed:", err);
    console.error("Coords sample:", coords?.slice(0, 4));
    return null;
  }
}


export function CreateShapegeometryWithMultipleVoidsxyz(mainringcoords, holesArray) {
  let geometry;
  const shape = new THREE.Shape();
  
  mainringcoords.forEach((coord, index) => {
    if (index === 0) {
      shape.moveTo(coord.x, coord.y);
    } else {
      shape.lineTo(coord.x, coord.y);
    }
  });
  
  if (holesArray && holesArray.length > 0) {
    holesArray.forEach(holeCoords => {
      const path = new THREE.Path();
      holeCoords.forEach((pt, i) => {
        if (i === 0) {
          path.moveTo(pt.x, pt.y);
        } else {
          path.lineTo(pt.x, pt.y);
        }
      });
      shape.holes.push(path);
    });
  }
  
  if (shape.curves.length > 0) {
    shape.closePath();
    geometry = new THREE.ShapeGeometry(shape);
    const height = 0;
    const positions = geometry.attributes.position;
    
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      positions.setXYZ(i, x, height, -y);
    }
    
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  }
  
  return geometry;
}

// export function CreateShapegeometry(coords) {
//   let geometry;
//   const shape = new THREE.Shape();
  
//   coords.forEach((coord, index) => {
//     if (index === 0) {
//       shape.moveTo(coord[0], coord[1]);
//     } else {
//       shape.lineTo(coord[0], coord[1]);
//     }
//   });
 
//   if (shape.curves.length > 0) {
//     shape.closePath();
//     geometry = new THREE.ShapeGeometry(shape);
//     const height = 0;
//     const positions = geometry.attributes.position;
    
//     for (let i = 0; i < positions.count; i++) {
//       const x = positions.getX(i);
//       const y = positions.getY(i);
//       positions.setXYZ(i, x, height, -y);
//     }  
    
//     geometry.computeVertexNormals();
//     geometry.computeBoundingBox();
//     geometry.computeBoundingSphere();
//   }
//   return geometry;
// }

export function CreateShapegeometryxyz(coords) {
  let geometry;
  const shape = new THREE.Shape();
  
  coords.forEach((coord, index) => {
    if (index === 0) {
      shape.moveTo(coord.x, coord.y);
    } else {
      shape.lineTo(coord.x, coord.y);
    }
  });
 
  if (shape.curves.length > 0) {
    shape.closePath();
    geometry = new THREE.ShapeGeometry(shape);
    const height = 0;
    const positions = geometry.attributes.position;
    
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      positions.setXYZ(i, x, height, -y);
    }  
    
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  }
  return geometry;
}
